/**
 * Д-17: «вышло обновление — обновите страницу».
 *
 * 🔴 Зачем. Бандл CRA живёт в открытой вкладке до перезагрузки. После окна
 * деплоя SSE переподключается сам и продолжает приносить сообщения в СТАРЫЙ
 * код: 15.09 (реально 02.09) владелец держал комнату вкладкой на телефоне
 * через окно C3, и сообщение участника с картинкой пришло голым текстом —
 * навсегда, потому что старый контроллер хранил при приёме только `text`, а
 * Olm расшифровывает копию один раз. Сигнала «вышла новая версия» не было ни
 * в /me, ни в SSE, ни в index.html.
 *
 * 🔴 Почему версия = хеш бандла из index.html, а не поле в `/api/v1/health`.
 * Версия сборки веба и версия бэкенда — разные вещи: окно бывает web-only
 * (бэкенд не перезапускается, `VERSION.txt` на сервере меняется, а процесс
 * его не читает), бывает backend-only. Единственный источник истины «какой
 * код сейчас ОТДАЁТ web-контейнер» — сам index.html: nginx (INF-5) отдаёт
 * его с `Cache-Control: no-cache`, и ссылка `/static/js/main.<hash>.js` в
 * нём меняется ровно тогда, когда контейнер заменён. Сравниваем хеш из
 * свежего index.html с хешем скрипта, который выполняется в этой вкладке. Ни
 * бэкенд, ни файлы версий, ни новые ручки не нужны; ответ на GET index.html —
 * ~1 КБ статики.
 *
 * Когда проверяем: при каждом (пере)подключении SSE (`noteStreamReady`, обе
 * машины — 1:1 и комната), при возврате вкладки на экран, при возврате сети
 * и раз в UPDATE_POLL_MS, пока вкладка видна. Не чаще раза в UPDATE_MIN_GAP_MS
 * (шторм реконнектов при лежащем бэкенде не должен превращаться в шторм
 * запросов). Один живой запрос за раз.
 *
 * Чего НЕ делаем: автоперезагрузки. Ввод и черновик человека терять нельзя;
 * решение — кнопкой в плашке (`updateBanner.jsx`).
 *
 * Модуль намеренно чистый (без React): им пользуются контроллеры переписки
 * из `msg/`, а React-обёртка живёт отдельно. Состояние — одно на страницу:
 * вкладка одна, бандл в ней один, и две машины (у участника — 1:1 и комната)
 * обязаны видеть один и тот же ответ.
 */

/** Опрос index.html, пока вкладка видна. */
export const UPDATE_POLL_MS = 2 * 60_000;
/** Не чаще: реконнекты SSE и события видимости сливаются в одну проверку. */
export const UPDATE_MIN_GAP_MS = 30_000;
/** Что читаем: сам документ, мимо любого кэша (nginx и так `no-cache`). */
export const INDEX_URL = '/index.html';

const BUNDLE_RE = /\/static\/js\/main\.([0-9a-f]+)\.js\b/;

/** Хеш главного бандла CRA из HTML/URL или null, если ссылки нет. */
export function parseBundleId(html) {
  const m = BUNDLE_RE.exec(String(html ?? ''));
  return m ? m[1] : null;
}

/**
 * Хеш бандла, который выполняется В ЭТОЙ вкладке — из тега <script> документа.
 * На dev-сервере CRA (`bundle.js`) и в jsdom его нет → null → сторож молчит.
 */
export function currentBundleId(doc = globalThis.document) {
  const scripts = doc?.scripts;
  if (!scripts) return null;
  for (let i = 0; i < scripts.length; i += 1) {
    const id = parseBundleId(scripts[i].getAttribute('src') || '');
    if (id) return id;
  }
  return null;
}

// ------------------------------------------------------------ состояние

const initialState = () => ({
  // lastCheckAt: null = ещё ни разу не спрашивали (первый повод — без паузы)
  current: null, deployed: null, updateAvailable: false, lastCheckAt: null,
});
let state = initialState();
const listeners = new Set();
let inflight = null;
let deps = null;      // { fetchImpl, indexUrl, now } — есть только у живого сторожа
let watchers = 0;
let detach = null;

const setState = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((fn) => {
    // Чужой слушатель не имеет права уронить сторожа и соседей по списку
    try { fn(state); } catch { /* его ошибка — его дело */ }
  });
};

export function getUpdateState() { return state; }

/** Подписка на смену состояния; возвращает отписку. */
export function subscribeUpdate(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Одна проверка. Молчит, если сторож не запущен, хеш вкладки неизвестен,
 * обновление уже найдено (состояние липкое до перезагрузки) или прошло
 * меньше UPDATE_MIN_GAP_MS с прошлой (кроме `force`). Ошибки сети и чужой
 * HTML (502 от edge без ссылки на бандл) — не повод для плашки: ждём
 * следующего повода.
 */
export function checkForUpdate({ force = false } = {}) {
  if (!deps || !state.current || state.updateAvailable) return Promise.resolve(state);
  if (inflight) return inflight;
  if (!force && state.lastCheckAt !== null && deps.now() - state.lastCheckAt < UPDATE_MIN_GAP_MS) {
    return Promise.resolve(state);
  }
  inflight = (async () => {
    // 🔴 Всё — под try: бросивший слушатель или fetch не имеет права оставить
    // `inflight` отклонённым навсегда (ревью с.89, Low) — иначе каждый
    // следующий повод возвращал бы тот же мёртвый промис до перезагрузки.
    try {
      setState({ lastCheckAt: deps.now() });
      const res = await deps.fetchImpl(deps.indexUrl, { cache: 'no-store', credentials: 'same-origin' });
      if (!res?.ok) return state;
      const deployed = parseBundleId(await res.text());
      if (!deployed) return state;
      setState({ deployed, updateAvailable: deployed !== state.current });
    } catch {
      /* нет сети — следующая попытка по таймеру или по событию */
    } finally {
      inflight = null;
    }
    return state;
  })();
  return inflight;
}

/**
 * Сигнал от машины переписки: подписка SSE встала (и после каждого
 * переподключения). Проверка — с общим ограничением частоты.
 */
export function noteStreamReady() {
  return checkForUpdate();
}

/**
 * Запуск сторожа. Считает вызовы: страница участника монтирует плашку в одном
 * месте, кабинет — в другом, но слушатели и таймер нужны ровно одни. Вернёт
 * функцию остановки; последняя остановка снимает слушатели и таймер.
 */
export function startUpdateWatch({
  doc = globalThis.document,
  win = globalThis.window,
  fetchImpl = (...args) => globalThis.fetch(...args),
  now = Date.now,
  intervalMs = UPDATE_POLL_MS,
  indexUrl = INDEX_URL,
} = {}) {
  watchers += 1;
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    watchers = Math.max(0, watchers - 1); // сброс между тестами не уводит счётчик в минус
    if (watchers === 0) {
      detach?.();
      detach = null;
      deps = null;
    }
  };
  if (watchers > 1) return stop;

  deps = { fetchImpl, indexUrl, now };
  setState({ current: currentBundleId(doc) });
  if (!state.current) return stop; // dev-сервер / тест: сравнивать не с чем

  const onVisible = () => { if (doc.visibilityState !== 'hidden') checkForUpdate(); };
  const onOnline = () => checkForUpdate();
  const timer = setInterval(() => {
    if (doc.visibilityState !== 'hidden') checkForUpdate();
  }, intervalMs);
  doc.addEventListener?.('visibilitychange', onVisible);
  win?.addEventListener?.('online', onOnline);
  detach = () => {
    clearInterval(timer);
    doc.removeEventListener?.('visibilitychange', onVisible);
    win?.removeEventListener?.('online', onOnline);
  };
  return stop;
}

/** Только для тестов: состояние модуля — одно на страницу, между тестами его надо обнулять. */
export function _resetUpdateWatchForTests() {
  detach?.();
  detach = null;
  deps = null;
  watchers = 0;
  inflight = null;
  listeners.clear();
  state = initialState();
}
